import { Accordion, AccordionSection, Layout, Text } from '@oneaudi/unified-web-alpha-components';
import React, { useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import {
	StyledAddedHeader,
	StyledAutomaticChangeSection,
	StyledChildrenContainer,
	StyledDecisionContainer,
	StyledHeaderContent,
} from './styles/decision-group-styles';

import {
	AutomaticChangeItemCard,
	ConflictItemCard,
	TriggerItemCard,
} from '~/app/pages/configurator/components/conflict-dialog/components/conflict-items';
import {
	ConflictItem,
	useConflictHandler,
} from '~/app/pages/configurator/components/conflict-dialog/use-conflict-handler';
import { ConfiguredCarFeatureFragment } from '~/graphql/__generated__/graphql';

export function DecisionGroup({
	groupIndex,
	group,
	isItemLoading,
	isItemSelected,
	onItemChecked,
	isChangingConflictSolutions,
	onAccordionClick,
}: {
	groupIndex: number;
	group: ConfiguredCarFeatureFragment[];
	isChangingConflictSolutions: boolean;
	isItemSelected: ReturnType<typeof useConflictHandler>['isItemSelected'];
	isItemLoading: ReturnType<typeof useConflictHandler>['isItemLoading'];
	onItemChecked: ReturnType<typeof useConflictHandler>['changeSelection'];
	onAccordionClick: (open: boolean, solutionGroup: string, label: string) => void;
}) {
	const { i18n } = useI18n();

	const allItemsSameGroup = () => group.every((item) => item.family.name === group[0].family.name);
	const allItemsSameCategory = () => group.every((item) => item.featureType === group[0].featureType);

	const groupName = allItemsSameGroup()
		? group[0].family.name
		: allItemsSameCategory()
			? i18n(`ngc.types.ConfiguredCarFeature.${group[0].featureType}`)
			: '';

	const selectedItem = group.find((g) => g.status.selected);
	const accordionItems = group.filter((g) => !g.status.selected);
	const [accordionOpen, setAccordionOpen] = useState(false);

	return (
		<StyledDecisionContainer>
			<Text
				margin={[0, 0, '16px', 0]}
				variant={{ type: 'copy', order: '1', style: 'normal' }}
				className="overflow-hidden">
				{`${i18n('ngc.common.change')} ${groupIndex + 1}: ${groupName}`}
			</Text>

			{selectedItem && (
				<ConflictItemList>
					<TriggerItemCard item={selectedItem} />
					<Accordion>
						<AccordionSection
							headingTag={'h3'}
							open={accordionOpen}
							onToggle={() => {
								setAccordionOpen(!accordionOpen);
								onAccordionClick(
									accordionOpen,
									groupName,
									i18n('ngc.conflict.showOtherOptions', { count: accordionItems.length }),
								);
							}}
							label={
								<Text className="label" variant={{ type: 'copy', order: '2', style: 'normal' }}>
									{i18n('ngc.conflict.showOtherOptions', { count: accordionItems.length })}
								</Text>
							}>
							<Layout margin={[0, 0, '24px', 0]} direction={'column'} rowGap="16px">
								{accordionItems.map((item, itemIndex) => {
									return (
										<ConflictItemCard
											key={item.pr7}
											item={item}
											disabled={isItemSelected(item) || isChangingConflictSolutions}
											loading={isItemLoading(groupIndex, itemIndex)}
											selected={isItemSelected(item)}
											onChecked={() => onItemChecked(item, groupIndex, itemIndex)}
										/>
									);
								})}
							</Layout>
						</AccordionSection>
					</Accordion>
				</ConflictItemList>
			)}
		</StyledDecisionContainer>
	);
}

export function AutomaticChangesSection({
	items,
	title,
	subtitle,
}: {
	title: string;
	subtitle?: string;
	items: ConflictItem[];
}) {
	return (
		<StyledAutomaticChangeSection>
			<StickySectionHeader title={title} subtitle={subtitle} />
			<ConflictItemList>
				{items.map((item) => (
					<AutomaticChangeItemCard key={item.pr7} item={item} />
				))}
			</ConflictItemList>
		</StyledAutomaticChangeSection>
	);
}

function StickySectionHeader({
	title,
	subtitle,
	collapseButton,
}: {
	title: string;
	subtitle?: string;
	collapseButton?: React.ReactNode;
}) {
	return (
		<StyledAddedHeader>
			<StyledHeaderContent>
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }} className="title overflow-hidden">
					{title}
				</Text>
				{subtitle && (
					<Text variant={{ type: 'copy', order: '2', style: 'normal' }} className="overflow-hidden">
						{subtitle}
					</Text>
				)}
			</StyledHeaderContent>

			{collapseButton}
		</StyledAddedHeader>
	);
}

function ConflictItemList({ children }: { children: React.ReactNode }) {
	return <StyledChildrenContainer>{children}</StyledChildrenContainer>;
}
